import { loadCommentByIdAtom } from '@src/store/comments/comments';
import { loadPostByIdentifierAtom } from '@src/store/posts/posts';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import type { ContentEntityType } from '../model'; // Ensure ContentEntityType is imported

export type EntityLoadStatus = 'idle' | 'loading' | 'success' | 'error';

export interface UseEntityLoaderProps {
    entityType: ContentEntityType;
    entityIdentifier?: string; // identifier could be a postId postSlug, commentId,
}

function getLoadAtomForEntityType(entityType: string) {
    if (entityType === 'Post') return loadPostByIdentifierAtom;
    if (entityType === 'Comment') return loadCommentByIdAtom;
    throw new Error(`No load atom configured for entity type: ${entityType}`);
}

export const useEntityLoader = (props: UseEntityLoaderProps) => {
    const { entityType, entityIdentifier } = props;

    const loadEntityAtom = getLoadAtomForEntityType(entityType);
    const triggerLoadInternal = useSetAtom(loadEntityAtom);

    const [entityId, setEntityId] = useState('');
    const [loadStatus, setLoadStatus] = useState<EntityLoadStatus>('idle');
    const [error, setError] = useState<Error | null>(null);

    const load = useCallback(async () => {
        if (!entityIdentifier) return console.warn(`[useEntityLoader] No load entityIdentifier provided for ${entityType}.`);

        setLoadStatus('loading');
        setError(null);
        try {
            const loadedEntity = await triggerLoadInternal(entityIdentifier);
            setEntityId(loadedEntity.id);
            setLoadStatus('success');
            return loadedEntity;
        } catch (e) {
            const loadError = e instanceof Error ? e : new Error(`Failed to load ${entityType}: ${entityIdentifier}`);
            setError(loadError);
            setLoadStatus('error');
            setEntityId('');
            console.error(`[useEntityLoader] Error loading ${entityType} "${entityIdentifier}":`, loadError);
            return;
        }
    }, [entityType, entityIdentifier, triggerLoadInternal]);

    useEffect(() => {
        setEntityId('');
        setLoadStatus('idle');
        setError(null);
    }, [entityIdentifier, load]);

    return {
        entityId,
        load,
        loadStatus,
        error,
    };
};
